import { FormEvent, useContext, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import i18n from '@nextroll/ar-i18n';
// @ts-ignore - no types for ar-style-base
import { InputGroup } from '@nextroll/ar-style-base';

import {
    changeEmailFactory,
    updateEmailFieldFactory,
} from '../../actions/email-verification/EmailVerificationActions';
import InlineFeedback from '../../components/InlineFeedback';
import { ApiError } from '../../components/errors';
import { API_STATUSES } from '../../constants';
import ThemeWrapperContext from '../../contexts/ThemeWrapperContext';
import EmailVerificationContext, {
    INITIAL_EMAIL_VERIFICATION_STATE,
} from '../../contexts/email-verification/EmailVerificationContext';
import EmailVerificationReducer from '../../reducers/email-verification/EmailVerificationReducer';
import { validate } from '../sign-up/fields/Email';

export const _ChangeEmailForm = () => {
    const { state, dispatch } = useContext(EmailVerificationContext);
    const { supportEmail } = useContext(ThemeWrapperContext);
    const navigate = useNavigate();
    const field = state.get('emailField');
    const submitState = state.get('changeEmailSubmit');
    const disabled = !field.valid;

    const changeEmailSubmit = changeEmailFactory(dispatch, field.value);
    const updateEmailField = updateEmailFieldFactory(dispatch);

    useEffect(() => {
        if (submitState.status === API_STATUSES.SUCCESS) {
            navigate(`../verify-email`);
        }
    }, [submitState.status]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const err = validate(value);
        updateEmailField({
            value,
            valid: err === null,
            error: err,
        });
    };
    const onFormSubmit = (e: FormEvent) => {
        e.preventDefault();
        onSubmit && onSubmit();
        return false;
    };
    const onSubmit =
        !disabled && submitState.status !== API_STATUSES.IN_PROGRESS
            ? changeEmailSubmit
            : undefined;

    return (
        <div className='eh-card-block eh-form-change-email'>
            <form onSubmit={onFormSubmit}>
                <div className='eh-card-header'>
                    <h3 className='eh-card-header-title'>
                        {i18n.gettext('Change Email')}
                    </h3>
                    <p>
                        {i18n.gettext(
                            "Didn't receive an email or want to change?"
                        )}
                    </p>
                    <p>
                        {i18n.gettext(
                            'It may take a few minutes for the email to reach your inbox or may end up in your spam folder. Still nothing? Re-enter your email and try again.'
                        )}
                    </p>
                    {field.showError && (
                        <ApiError
                            error={submitState.error}
                            supportEmailAddress={supportEmail}
                        />
                    )}
                </div>
                <div className='eh-card-body'>
                    <InputGroup
                        i18n={i18n}
                        classNames='eh-input-email'
                        inputProps={{
                            autoComplete: 'email',
                            name: 'email',
                            placeholder: i18n.gettext('Your Email'),
                            type: 'email',
                            value: field.value,
                            onChange,
                            onFocus: () =>
                                updateEmailField({ showError: false }),
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                onChange(e);
                                updateEmailField({ showError: true });
                            },
                        }}
                        validationState={
                            !field.valid && field.showError
                                ? InputGroup.VALIDATION_ERROR
                                : undefined
                        }
                        help={field.showError ? field.error : undefined}
                    />
                </div>
                <div className='eh-card-footer'>
                    <button
                        type='submit'
                        className='btn btn-primary btn-block'
                        disabled={disabled}
                    >
                        {i18n.gettext('Save')}
                        <InlineFeedback status={submitState.status} />
                    </button>
                </div>
            </form>
        </div>
    );
};

const ChangeEmailForm = () => {
    const [state, dispatch] = useReducer(
        EmailVerificationReducer,
        INITIAL_EMAIL_VERIFICATION_STATE
    );

    return (
        <EmailVerificationContext.Provider value={{ state, dispatch }}>
            <_ChangeEmailForm />
        </EmailVerificationContext.Provider>
    );
};

export default ChangeEmailForm;
